import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check, ChevronRight, Clock, Copy, FileImage, FileText, Film, ImagePlus,
  Layers, Loader2, Music2, Pause, Play, Plus, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { compressReceipt, postFormWithRetry } from "@/lib/receipt";

const MAX_PHOTOS = 30;
const MIN_PHOTOS = 1;
const TEMPO_DESCS = {
  auto: "Сам підберу зручний темп — кадри можуть повторюватися",
  one: "Кожне фото один раз, розтягнуте на всю пісню",
  "4": "Швидка зміна кадрів — динамічне відео",
  "6": "Жвавий темп — універсальний варіант",
  "8": "Спокійний темп — встигаєш роздивитись",
  "10": "Повільний темп — для ліричних пісень",
};

const TRANSITIONS = [
  { id: "fade", label: "Розчинення" },
  { id: "slide", label: "Зсув" },
  { id: "circle", label: "Коло" },
  { id: "blur", label: "Розмиття" },
  { id: "zoom", label: "Наїзд" },
  { id: "cover", label: "Шторка" },
  { id: "mix", label: "Мікс (усі)" },
];

const FORMATS = [
  { id: "9:16", desc: "Для Stories / reels" },
  { id: "1:1", desc: "Для соцмереж" },
  { id: "16:9", desc: "Для широких екранів" },
];

function StepHead({ n, title, done, action, onAction }) {
  return (
    <div className="vid-step-head">
      <span className={"vid-num" + (done ? " is-done" : "")}>
        {done ? <Check size={14} /> : n}
      </span>
      <b className="vid-step-title">{title}</b>
      {action && (
        <button type="button" className="vid-step-link" onClick={onAction}>{action}</button>
      )}
    </div>
  );
}

export default function VideoCreate({ songs = [], onSongsRefresh, onGoCreate, trackApp }) {
  const ready = useMemo(
    () => (songs || []).filter((s) => (s.tracks || []).length > 0),
    [songs],
  );
  const [song, setSong] = useState(null);
  const [trackIdx, setTrackIdx] = useState(0);
  const [playingKey, setPlayingKey] = useState("");
  const [photos, setPhotos] = useState([]);
  const [format, setFormat] = useState("9:16");
  const [transition, setTransition] = useState("fade");
  const [transOpen, setTransOpen] = useState(false);
  const [mode, setMode] = useState("own");        // own | paid
  const [audio, setAudio] = useState(null);       // {path,name,duration}
  const [audioBusy, setAudioBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [card, setCard] = useState("");
  const [copied, setCopied] = useState(false);
  const [freeLeft, setFreeLeft] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [preview, setPreview] = useState("");
  const [photosBusy, setPhotosBusy] = useState(false);
  const [sessionJob, setSessionJob] = useState(false);
  const [tempoOpen, setTempoOpen] = useState(false);
  const [pace, setPace] = useState("one");
  const [songDur, setSongDur] = useState(0);
  const payRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState("");
  const [uploadPct, setUploadPct] = useState(null);
  const [err, setErr] = useState("");
  const [job, setJob] = useState(null);

  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const audioFileRef = useRef(null);
  const receiptFileRef = useRef(null);
  const dragRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(-1);
  const [overIdx, setOverIdx] = useState(-1);
  const floatRef = useRef(null);
  const rafRef = useRef(0);
  const lastPos = useRef({ x: 0, y: 0 });
  const photosRef = useRef([]);
  photosRef.current = photos;

  // Последнее видео юзера (переживает перезагрузку страницы).
  useEffect(() => {
    api.get("/public/payment-card")
      .then((r) => setCard(r.data?.card || ""))
      .catch(() => {});
    api.get("/public/app/videos")
      .then((r) => {
        setFreeLeft(r.data?.free_left ?? null);
        const v = (r.data?.items || [])[0];
        if (v) setJob(v);
      })
      .catch(() => {});
    onSongsRefresh?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Поллинг статуса, пока идёт обработка.
  useEffect(() => {
    if (!job?.id || !["awaiting", "queued", "processing"].includes(job.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/public/app/video/${job.id}`);
        setJob((prev) => ({ ...(prev || {}), ...r.data }));
      } catch { /* повторим */ }
    }, 5000);
    return () => clearInterval(t);
  }, [job?.id, job?.status]);

  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setErr("");
    const room = MAX_PHOTOS - photosRef.current.length;
    if (room <= 0) { setErr(`Максимум ${MAX_PHOTOS} фото.`); return; }
    setPhotosBusy(true);
    try {
      const accepted = [];
      for (const f of files.slice(0, room)) {
        if (!(f.type || "").startsWith("image/")) {
          setErr("Підтримуються лише фото (jpg, png, webp).");
          continue;
        }
        // Ужимаем сильнее: 50 фото должны влезать в лимит загрузки.
        const small = await compressReceipt(f, { maxSide: 1600, quality: 0.9 });
        accepted.push({ file: small, url: URL.createObjectURL(small), name: f.name });
      }
      if (accepted.length) {
        setPhotos((prev) => [...prev, ...accepted].slice(0, MAX_PHOTOS));
        trackApp?.("відео: додано фото", String(photosRef.current.length + accepted.length));
      }
      if (files.length > room) setErr(`Максимум ${MAX_PHOTOS} фото — зайві не додані.`);
    } finally {
      setPhotosBusy(false);
    }
  };

  const onThumbDown = (e, i) => {
    if (busy) return;
    const start = { x: e.clientX, y: e.clientY };
    dragRef.current = { i, start, active: false, timer: setTimeout(() => {
      if (!dragRef.current) return;
      dragRef.current.active = true;
      lastPos.current = { x: start.x, y: start.y };
      setDragIdx(i);
      setOverIdx(i);
      requestAnimationFrame(() => {
        const el = floatRef.current;
        if (el) {
          el.style.transform = `translate3d(${start.x - 36}px, ${start.y - 36}px, 0) scale(1.05)`;
        }
      });
      try { navigator.vibrate?.(10); } catch { /* noop */ }
    }, 220) };
  };
  const onThumbMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.start.x;
    const dy = e.clientY - d.start.y;
    if (!d.active) {
      if (Math.hypot(dx, dy) > 12) { clearTimeout(d.timer); dragRef.current = null; }
      return;
    }
    lastPos.current = { x: e.clientX, y: e.clientY };
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const p = lastPos.current;
      const el = floatRef.current;
      if (el) {
        el.style.transform = `translate3d(${p.x - 36}px, ${p.y - 36}px, 0) scale(1.05)`;
      }
      const t = document.elementFromPoint(p.x, p.y)?.closest?.("[data-thumb]");
      const idx = t ? Number(t.getAttribute("data-thumb")) : -1;
      if (idx >= 0) setOverIdx((prev) => (prev === idx ? prev : idx));
    });
  };
  const onThumbUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.timer) clearTimeout(d.timer);
    if (d?.active && overIdx >= 0 && overIdx !== d.i) {
      setPhotos((prev) => {
        const arr = [...prev];
        const [moved] = arr.splice(d.i, 1);
        arr.splice(overIdx, 0, moved);
        return arr;
      });
      trackApp?.("відео: порядок фото змінено", String(overIdx + 1));
    }
    setDragIdx(-1);
    setOverIdx(-1);
  };

  const pickAudio = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const okExt = /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(f.name || "");
    if (!(f.type || "").startsWith("audio/") && !okExt) {
      setErr("Оберіть аудіофайл (mp3, wav, m4a).");
      return;
    }
    if (f.size > 20 * 1024 * 1024) { setErr("Файл завеликий — до 20 МБ."); return; }
    setAudioBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await postFormWithRetry("/public/app/video/upload-audio", fd);
      setAudio({ path: r.data?.path, name: f.name, duration: r.data?.duration || 0 });
      trackApp?.("відео: завантажено музику", f.name);
    } catch (e2) {
      const d = e2?.response?.data?.detail || "";
      if (d === "audio-duration") setErr("Пісня має бути від 10 секунд до 10 хвилин.");
      else setErr("Не вдалося завантажити музику — спробуйте ще раз.");
    } finally {
      setAudioBusy(false);
    }
  };

  const pickReceipt = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(f.type)
        || f.size > 10 * 1024 * 1024) {
      setErr("Чек має бути фото (jpg, png, webp) або PDF до 10 МБ.");
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setReceipt(f);
    setPreview(URL.createObjectURL(f));
    setErr("");
    const small = await compressReceipt(f);
    if (small !== f) setReceipt(small);
  };

  const copyCard = async () => {
    try {
      await navigator.clipboard.writeText(card);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { setCopied(false); }
  };

  const removePhoto = (i) => {
    setPhotos((prev) => {
      const p = prev[i];
      if (p?.url) URL.revokeObjectURL(p.url);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const playTrack = (s, ti) => {
    const el = audioRef.current;
    if (!el || !s) return;
    const key = `${s.share_code}:${ti}`;
    if (!el.paused && playingKey === key) { el.pause(); return; }
    el.src = s.tracks?.[ti]?.url || "";
    el.play().then(() => setPlayingKey(key)).catch(() => {});
  };

  const playToggle = () => playTrack(song, trackIdx);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const url = song?.tracks?.[trackIdx]?.url || "";
    if (url && el.getAttribute("src") !== url) {
      el.src = url;  // preload=metadata прочитает длительность → songDur
    }
  }, [song, trackIdx]);

  const pickSong = (s, ti = 0) => {
    setSong(s);
    setTrackIdx(ti);
    setPickerOpen(false);
    setPlaying(false);
    setPlayingKey("");
    trackApp?.("відео: обрано пісню", `${s?.song_title || ""} · варіант ${ti + 1}`);
  };

  const paid = mode === "paid";
  const renderInFlight = job && ["queued", "processing"].includes(job.status);
  const busyJob = ["awaiting", "queued", "processing"].includes(job?.status);
  const musicDur = paid ? (audio?.duration || 0) : songDur;
  const loopsFor = (p) => {
    const n = photos.length;
    if (!musicDur || n < 2) return 1;
    if (p === "one") return 1;
    if (p === "auto") {
      return Math.max(1, Math.min(6, Math.round(musicDur / n / 7)));
    }
    return Math.max(1, Math.min(8, Math.ceil(musicDur / (n * Number(p)))));
  };
  const tempoLoops = loopsFor(pace);
  const tempoSec = musicDur && photos.length
    ? Math.max(1, Math.round(musicDur / (photos.length * tempoLoops))) : 0;
  const canSubmit = paid
    ? (!!audio && photos.length >= MIN_PHOTOS && !busy && !busyJob)
    : (!!song && photos.length >= MIN_PHOTOS && !busy && !renderInFlight
       && (freeLeft === null || freeLeft > 0));

  const openTempo = () => {
    if (!canSubmit) return;
    setErr("");
    // Одно фото — темпу нема чого обирати: одразу далі.
    if (photos.length < 2) {
      if (paid) {
        setPayOpen(true);
        setTimeout(() => payRef.current?.scrollIntoView(
          { behavior: "smooth", block: "center" }), 100);
      } else {
        submit();
      }
      return;
    }
    setTempoOpen(true);
  };

  const confirmTempo = () => {
    setTempoOpen(false);
    if (paid) {
      setPayOpen(true);
      setTimeout(() => payRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } else {
      submit();
    }
  };

  const submit = async () => {
    if (paid && !payOpen) { setPayOpen(true); return; }
    if (paid && !receipt) { setErr("Прикріпіть чек оплати."); return; }
    if (!canSubmit) return;
    setBusy("submit");
    setErr("");
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("format", format);
      fd.append("transition", transition);
      fd.append("pace", String(pace));
      fd.append("loops", String(tempoLoops));
      if (paid) {
        fd.append("mode", "paid");
        fd.append("music_path", audio.path || "");
        if (receipt) fd.append("receipt", receipt);
      } else {
        fd.append("song_code", song.share_code || "");
        fd.append("track_idx", String(trackIdx));
      }
      photos.forEach((p) => fd.append("files", p.file));
      const r = await postFormWithRetry("/public/app/video", fd, { onProgress: setUploadPct });
      setJob({ id: r.data?.id, status: r.data?.status || "queued", format });
      setSessionJob(true);
      if (!paid && freeLeft !== null) setFreeLeft(Math.max(0, freeLeft - 1));
      trackApp?.("відео: створено", `${paid ? "платне" : "безкоштовне"} · ${photos.length} фото`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "render-in-progress") {
        setErr("Відео вже створюється — дочекайтеся завершення.");
      } else if (detail === "video-limit") {
        setErr("Ліміт безкоштовних відео на добу вичерпано — спробуйте завтра або оберіть платне.");
        setFreeLeft(0);
      } else if (detail === "photos-count") setErr("Потрібно від 1 до 30 фото.");
      else if (detail === "photo-invalid") setErr("Одне з фото не підтримується — спробуйте інші.");
      else if (detail === "music-invalid") setErr("Музику не прийнято — завантажте ще раз.");
      else if (detail === "receipt-required" || detail === "receipt-invalid") {
        setErr("Прикріпіть чек оплати (фото або PDF).");
      } else if (detail === "song-not-found") setErr("Пісню не знайдено — виберіть іншу.");
      else setErr("Не вдалося створити відео. Перевірте зв'язок і спробуйте ще раз.");
    } finally {
      setBusy("");
      setUploadPct(null);
    }
  };

  const processing = job && ["queued", "processing"].includes(job.status);
  const failed = job && job.status === "failed";

  return (
    <div className="vid-page tab-swap" data-testid="video-page">
      <audio ref={audioRef} preload="metadata"
        onEnded={() => { setPlaying(false); setPlayingKey(""); }}
        onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)}
        onLoadedMetadata={(e) => setSongDur(e.target.duration || 0)} />
      <div className="vid-head">
        <h1>Створити відео</h1>
        <p>Додай фото та отримай відео під свою пісню</p>
      </div>

      <div className="vid-modes" role="tablist">
        <button type="button" role="tab" data-testid="mode-own"
          className={"vid-mode" + (!paid ? " is-on" : "")}
          onClick={() => { setMode("own"); setPayOpen(false); setErr(""); }}>
          Ваша музика
        </button>
        <button type="button" role="tab" data-testid="mode-paid"
          className={"vid-mode is-badged" + (paid ? " is-on" : "")}
          onClick={() => { setMode("paid"); setErr(""); }}>
          Будь-яка музика
          <span className="vid-mode-badge">99 грн</span>
        </button>
      </div>

      {/* Шаг 1 — пісня */}
      <section className="vid-step glass-surface">
        <StepHead n="1" title={paid ? "Пісня з пристрою" : "Вибери пісню"}
          done={paid ? !!audio : !!song}
          action={!paid && song ? "Змінити" : undefined}
          onAction={() => setPickerOpen(true)} />
        {paid ? (
          <>
            <input ref={audioFileRef} type="file"
              accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,audio/*"
              className="hidden" onChange={pickAudio} data-testid="video-audio-input" />
            <button type="button" className="vid-drop" disabled={audioBusy}
              onClick={() => audioFileRef.current?.click()}>
              {audioBusy ? <Loader2 size={24} className="spin" /> : <Music2 size={24} />}
              <b>{audio
                ? (audio.name.length > 15 ? audio.name.slice(0, 15) + "…" : audio.name)
                : "Завантажте пісню з пристрою"}</b>
              <span>{audio
                ? `${Math.round(audio.duration || 0)} с · натисніть, щоб змінити`
                : "mp3, wav, m4a · до 20 МБ · 10с–10хв"}</span>
            </button>
          </>
        ) : song ? (
          <div className="vid-song">
            <span className="vid-preview"><Music2 size={22} /></span>
            <span className="vid-song-info">
              <b>{song.song_title || "Ваша пісня"}</b>
              <i>{[song.genre_label, (song.created_at || "").slice(0, 10),
                   `Варіант ${trackIdx + 1}`].filter(Boolean).join(" · ")}</i>
            </span>
            <button type="button" className="st-play" onClick={playToggle}
              aria-label={playing ? "Пауза" : "Грати"}>
              {playing ? <Pause size={17} color="#fff" />
                : <Play size={17} color="#fff" fill="#fff" />}
            </button>
          </div>
        ) : (
          <button type="button" className="vid-btn-ghost" data-testid="video-pick-song"
            onClick={() => setPickerOpen(true)}>
            <Music2 size={17} /> Обрати пісню
          </button>
        )}
      </section>

      {/* Шаг 2 — фото */}
      <section className={"vid-step glass-surface"
        + ((paid ? audio : song) ? "" : " is-dim")}>
        <StepHead n="2" title="Додай фото" done={photos.length >= MIN_PHOTOS}
          action={photosBusy
            ? <Loader2 size={13} className="spin" />
            : `${photos.length} з ${MAX_PHOTOS}`} />
        <input ref={fileRef} type="file" accept="image/*" multiple
          className="hidden" onChange={pickPhotos} data-testid="video-photos-input" />
        <button type="button" className="vid-drop"
          disabled={(paid ? !audio : !song) || photos.length >= MAX_PHOTOS}
          onClick={() => fileRef.current?.click()}>
          <ImagePlus size={24} />
          <b>Додай фото або колаж</b>
          <span>Від 1 до {MAX_PHOTOS} фото</span>
        </button>
        {photos.length > 0 && (
          <div className="vid-thumbs">
            {photos.map((p, i) => (
              <span className={"vid-thumb" + (dragIdx === i ? " is-drag" : "")
                + (overIdx === i && dragIdx >= 0 && dragIdx !== i ? " is-over" : "")}
                key={p.url} data-thumb={i}
                onPointerDown={(e) => onThumbDown(e, i)}
                onPointerMove={onThumbMove}
                onPointerUp={onThumbUp}
                onPointerCancel={onThumbUp}>
                <img src={p.url} alt="" draggable={false} />
                {dragIdx !== i && (
                  <button type="button" onClick={() => removePhoto(i)} aria-label="Видалити">
                    <X size={12} />
                  </button>
                )}
              </span>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button type="button" className="vid-thumb-add"
                onClick={() => fileRef.current?.click()}>
                <Plus size={18} /> Ще фото
              </button>
            )}
          </div>
        )}
      </section>

      {/* Шаг 3 — формат */}
      <section className={"vid-step glass-surface"
        + (photos.length >= MIN_PHOTOS ? "" : " is-dim")}>
        <StepHead n="3" title="Формат відео" done={photos.length >= MIN_PHOTOS} />
        <div className="vid-formats">
          {FORMATS.map((f) => (
            <button type="button" key={f.id}
              className={"vid-format" + (format === f.id ? " is-sel" : "")}
              onClick={() => { setFormat(f.id); trackApp?.("відео: формат", f.id); }}>
              <span className="vid-ratio" data-ratio={f.id} />
              <b>{f.id}</b>
              <i>{f.desc}</i>
              {format === f.id && <span className="vid-format-check"><Check size={11} /></span>}
            </button>
          ))}
        </div>
        <button type="button" className="vid-trans-btn" data-testid="video-transition"
          disabled={photos.length < 2}
          onClick={() => photos.length >= 2 && setTransOpen(true)}>
          <Layers size={15} />
          Перехід між фото: <b>{TRANSITIONS.find((t) => t.id === transition)?.label}</b>
        </button>
      </section>

      {/* Итог + кнопка (в платном режиме прячем, когда открыт оплатник) */}
      {!(paid && payOpen) && (
      <div className="vid-cta-block glass-surface">
        <div className="vid-summary">
          Фото: <b>{photos.length}</b> · Формат: <b>{format}</b>
        </div>
        <button type="button" className="vid-cta" disabled={!canSubmit}
          data-testid="video-submit" onClick={openTempo}>
          {busy === "submit" ? "Завантажуємо…"
            : renderInFlight ? "Відео створюється…"
            : paid ? "Далі"
            : freeLeft === 0 ? "Ліміт на добу вичерпано"
            : `Створити відео${freeLeft !== null ? ` · залишилось ${freeLeft}` : ""}`}
        </button>
        {busy === "submit" && (
          <div className="vid-progress" data-testid="video-progress">
            <div className="vid-progress-bar" style={{ width: `${uploadPct || 0}%` }} />
          </div>
        )}
        {err && <p className="vid-error">{err}</p>}
      </div>
      )}

      {paid && payOpen && !busyJob && (
        <div className="vid-pay glass-surface" data-testid="video-pay" ref={payRef}>
          <div className="vid-pay-head">
            <b>Оплата 99 ₴</b>
            <span>Перекажіть суму на картку та прикріпіть чек</span>
          </div>
          <div className="vid-pay-card">
            <span className="vid-pay-num">{card || "—"}</span>
            <button type="button" className="vid-pay-copy" onClick={copyCard}>
              {copied ? <Check size={15} /> : <Copy size={15} />}
              {copied ? "Готово" : "Копіювати"}
            </button>
          </div>
          <input ref={receiptFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden" onChange={pickReceipt} data-testid="video-receipt-input" />
          <button type="button" className="vid-drop" onClick={() => receiptFileRef.current?.click()}>
            {preview ? (
              receipt && receipt.type === "application/pdf" ? (
                <span className="vid-drop-in">
                  <FileText size={22} /> {receipt.name}
                  <span className="vid-drop-hint">PDF · до 10 МБ</span>
                </span>
              ) : (
                <img src={preview} alt="" className="vid-pay-preview" />
              )
            ) : (
              <>
                <FileImage size={24} />
                <b>Прикріпіть чек оплати</b>
                <span>jpg, png, webp або PDF · до 10 МБ</span>
              </>
            )}
          </button>
          <button type="button" className="vid-cta" disabled={busy === "submit" || !receipt}
            data-testid="video-paid-submit" onClick={submit}>
            {busy === "submit" ? "Надсилаємо…" : "Я сплатив(а)"}
          </button>
        </div>
      )}

      {/* Состояния задачи */}
      {processing && (
        <div className="vid-status glass-surface" data-testid="video-processing">
          <span className="vid-spin"><Film size={20} /></span>
          <b>Створюємо відео…</b>
          <p>Відео надійде до боту, а також буде доступне в розділі «Мої пісні» → «Відео».</p>
        </div>
      )}
      {job && job.status === "awaiting" && sessionJob && (
        <div className="vid-status glass-surface" data-testid="video-awaiting">
          <span className="vid-spin"><Clock size={20} /></span>
          <b>Чек на перевірці</b>
          <p>Після підтвердження відео автоматично надійде до боту.</p>
        </div>
      )}
      {job && job.status === "rejected" && sessionJob && (
        <div className="vid-status glass-surface is-fail" data-testid="video-rejected">
          <b>Чек відхилено</b>
          <p>Оплату не підтверджено. Спробуйте ще раз або напишіть у підтримку.</p>
        </div>
      )}
      {failed && sessionJob && (
        <div className="vid-status glass-surface is-fail" data-testid="video-failed">
          <b>Не вдалося створити відео</b>
          <p>{job.error || "Спробуйте ще раз — вибір збережено."}</p>
        </div>
      )}

      {/* Летящее превью при перетаскивании */}
      {dragIdx >= 0 && photos[dragIdx] && (
        <span ref={floatRef} className="vid-drag-float">
          <img src={photos[dragIdx].url} alt="" />
        </span>
      )}

      {/* Модалка темпа слайдов */}
      {tempoOpen && (
        <div className="epw-modal-wrap" role="dialog" aria-modal="true">
          <div className="epw-modal-back" onClick={() => setTempoOpen(false)} />
          <div className="epw-modal vid-tempo-modal" data-testid="tempo-modal">
            <div className="epw-modal-head">
              <div className="epw-modal-title">Темп слайдів</div>
              <button type="button" className="epw-iconbtn" onClick={() => setTempoOpen(false)}
                aria-label="Закрити"><X size={18} /></button>
            </div>
            <p className="vid-tempo-desc">
              Скільки секунд показувати кожне фото. Якщо фото мало — вони пройдуть
              по колу кілька разів, щоб заповнити пісню.
            </p>
            <div className="vid-tempo-chips">
              {[["one", "Без повторів"], ["auto", "Авто"], ["4", "4с"], ["6", "6с"], ["8", "8с"], ["10", "10с"]].map(([id, label]) => (
                <button type="button" key={id}
                  className={"vid-tempo-chip" + (pace === id ? " is-on" : "")}
                  data-testid={`tempo-${id}`}
                  onClick={() => setPace(id)}>{label}</button>
              ))}
            </div>
            <p className="vid-tempo-mean" data-testid="tempo-mean">{TEMPO_DESCS[pace]}</p>
            <div className="vid-tempo-calc" data-testid="tempo-calc">
              {tempoSec > 0
                ? <>Кадри пройдуть <b>{tempoLoops} {tempoLoops === 1 ? "раз" : "рази"}</b> · ~<b>{tempoSec}с</b> на кадр</>
                : <>Темп розрахуємо при створенні</>}
            </div>
            <button type="button" className="vid-trans-ok" data-testid="tempo-confirm"
              disabled={busy === "submit"} onClick={confirmTempo}>
              {busy === "submit" ? "Завантажуємо…" : (paid ? "Сплатити 99 ₴" : "Створити відео")}
            </button>
          </div>
        </div>
      )}

      {/* Шит переходов */}
      {transOpen && (
        <div className="epw-modal-wrap" role="dialog" aria-modal="true">
          <div className="epw-modal-back" onClick={() => setTransOpen(false)} />
          <div className="epw-modal vid-trans-modal" data-testid="transition-modal">
            <div className="epw-modal-head">
              <div className="epw-modal-title">Перехід між фото</div>
              <button type="button" className="epw-iconbtn" onClick={() => setTransOpen(false)}
                aria-label="Закрити"><X size={18} /></button>
            </div>
            <div className="vid-trans-grid">
              {TRANSITIONS.map((t) => (
                <button type="button" key={t.id}
                  className={"vid-trans-card" + (transition === t.id ? " is-sel" : "")}
                  data-testid={`transition-${t.id}`}
                  onClick={() => { setTransition(t.id); trackApp?.("відео: перехід", t.id); }}>
                  <span className="vid-tprev" data-t={t.id}>
                    <i className="vid-tprev-a"
                      style={photos[0]?.url ? { backgroundImage: `url(${photos[0].url})` } : undefined} />
                    <i className="vid-tprev-b"
                      style={(photos[1] || photos[0])?.url
                        ? { backgroundImage: `url(${(photos[1] || photos[0]).url})` } : undefined} />
                  </span>
                  <b>{t.label}</b>
                  {transition === t.id && <Check size={13} className="vid-trans-check" />}
                </button>
              ))}
            </div>
            <button type="button" className="vid-trans-ok" data-testid="transition-ok"
              onClick={() => setTransOpen(false)}>Готово</button>
          </div>
        </div>
      )}

      {/* Шит выбора песни */}
      {pickerOpen && (
        <div className="vid-sheet-wrap" onClick={() => setPickerOpen(false)}>
          <div className="vid-sheet glass-surface" onClick={(e) => e.stopPropagation()}>
            <div className="vid-sheet-head">
              <b>Мої пісні</b>
              <button type="button" onClick={() => setPickerOpen(false)} aria-label="Закрити">
                <X size={18} />
              </button>
            </div>
            {ready.length === 0 && (
              <div className="vid-sheet-empty">
                <p>Щоб створити відео з музикою — спочатку замовте пісню.</p>
                <button type="button" className="vid-empty-cta" data-testid="video-empty-create"
                  onClick={() => { setPickerOpen(false); onGoCreate?.(); }}>
                  <Music2 size={16} /> Створити пісню
                </button>
              </div>
            )}
            {ready.map((s) => (
              <div className="vid-pick-card glass-surface" key={s.share_code || s.id}>
                <div className="vid-pick-head">
                  <span className="vid-preview sm"><Music2 size={18} /></span>
                  <span className="vid-song-info">
                    <b>{s.song_title || "Ваша пісня"}</b>
                    <i>{[s.genre_label, (s.created_at || "").slice(0, 10)].filter(Boolean).join(" · ")}</i>
                  </span>
                </div>
                {(s.tracks || []).slice(0, 2).map((t, ti) => {
                  const isSel = song && song.share_code === s.share_code && trackIdx === ti;
                  const key = `${s.share_code}:${ti}`;
                  const isPlaying = playingKey === key && playing;
                  return (
                    <button type="button" key={key}
                      className={"vid-pick-row" + (isSel ? " is-sel" : "")}
                      onClick={() => pickSong(s, ti)}>
                      <span className="vid-vplay"
                        role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); playTrack(s, ti); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); playTrack(s, ti); } }}
                        aria-label={isPlaying ? "Пауза" : "Прослухати"}>
                        {isPlaying ? <Pause size={14} color="#fff" />
                          : <Play size={14} color="#fff" fill="#fff" />}
                      </span>
                      <span className="vid-pick-label">Варіант {ti + 1}</span>
                      {isSel
                        ? <Check size={18} className="vid-sheet-check" />
                        : <ChevronRight size={18} className="vid-pick-arrow" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
